/**
 * The Sales Kit's derived views (design salesVals L20044-20257 and L20542-20683), built
 * from the rows GET /sales/kit serves (sales_kit_sections). The copy itself lives in the
 * database; this file only groups the rows back into documents and sections and builds
 * the "In a call" index and the Objections table the way the design does.
 */
import type { SalesKitDoc, SalesKitSection } from '@aithora/shared';

export interface KitSection {
  name: string;
  step: string;
  body: string;
  quotes: [string, string][];
  bullets: string[];
  flag: string;
  objections: [string, string][];
  /** The objections here are stall points (kind 'stall'). */
  stall: boolean;
}
export interface KitDoc {
  doc: string;
  chip: 'CONFIDENTIAL' | 'INTERNAL' | 'SHAREABLE';
  note: string;
  sub: string;
  link: string;
  personalize: boolean;
  sections: KitSection[];
}

/**
 * Group the rows into documents in the Library's order: the served `docs` metadata when
 * there is one (sales_kit_docs), otherwise the order the server sorts them. Without metadata a
 * document is SHAREABLE when its rows are, INTERNAL otherwise, with no note, sub or link.
 */
export function buildKit(rows: SalesKitSection[], docs: SalesKitDoc[] | undefined): KitDoc[] {
  const byDoc = new Map<string, SalesKitSection[]>();
  for (const r of [...rows].sort((a, b) => a.sort - b.sort)) {
    const list = byDoc.get(r.doc) ?? [];
    list.push(r);
    byDoc.set(r.doc, list);
  }
  const order = docs?.length ? docs.map((d) => d.doc) : [...new Set(rows.map((r) => r.doc))];
  return order.map((name) => {
    const meta = docs?.find((d) => d.doc === name);
    const list = byDoc.get(name) ?? [];
    const sections: KitSection[] = [];
    const at = new Map<string, KitSection>();
    for (const r of list) {
      let s = at.get(r.section);
      if (!s) {
        s = { name: r.section, step: '', body: '', quotes: [], bullets: [], flag: '', objections: [], stall: false };
        at.set(r.section, s);
        sections.push(s);
      }
      if (r.step && !s.step) s.step = r.step;
      if (r.kind === 'body') s.body = s.body ? s.body + ' ' + r.text : r.text;
      else if (r.kind === 'say') s.quotes.push([r.label ?? '', r.text]);
      else if (r.kind === 'bullet') s.bullets.push(r.text);
      else if (r.kind === 'flag') s.flag = r.text;
      else if (r.kind === 'objection' || r.kind === 'stall') {
        s.objections.push([r.label ?? '', r.text]);
        if (r.kind === 'stall') s.stall = true;
      }
    }
    const chip: KitDoc['chip'] = meta
      ? (meta.classification.toUpperCase() as KitDoc['chip'])
      : list.some((r) => r.visibility === 'shareable')
        ? 'SHAREABLE'
        : 'INTERNAL';
    return { doc: name, chip, note: meta?.note ?? '', sub: meta?.sub ?? '', link: meta?.link ?? '', personalize: !!meta?.personalize, sections };
  });
}

/** One entry of the design's "In a call" index (IDX). */
export interface KitItem {
  doc: string;
  sec: string;
  label: string;
  text: string;
  say: boolean;
  obj?: boolean;
  stall?: boolean;
}

/** The design's index: per section its lines to say, its body, its bullets, its objections. */
export function kitIndex(kit: KitDoc[]): KitItem[] {
  const idx: KitItem[] = [];
  for (const d of kit) {
    for (const sec of d.sections) {
      for (const q of sec.quotes) idx.push({ doc: d.doc, sec: sec.name, label: q[0].replace(/ /g, ' '), text: q[1], say: true });
      if (sec.body) idx.push({ doc: d.doc, sec: sec.name, label: sec.name, text: sec.body, say: false });
      for (const b of sec.bullets) idx.push({ doc: d.doc, sec: sec.name, label: sec.name, text: b, say: false });
      for (const o of sec.objections) idx.push({ doc: d.doc, sec: sec.name, label: o[0], text: o[1], say: true, obj: true, stall: sec.stall });
    }
  }
  return idx;
}

/**
 * V9 pricing words (Andrew, Oct 28): the subscription is "$99 per occupied bed", never "$99 per
 * bed". The kit's rows come from the server (sales_kit_sections), which still says "per bed"
 * until its content is updated, so the console reads them in the current words.
 */
export function pricingWords(t: string): string {
  return t
    .replace(/(\$99) per bed\b/g, '$1 per occupied bed')
    .replace(/We charge the per-bed subscription, not a cut\./g, 'We charge the subscription per occupied bed.')
    .replace(/\bsimple per-bed subscription\b/g, 'simple subscription per occupied bed')
    .replace(/\ba per-bed subscription at\b/g, 'a subscription at')
    .replace(/\ba per-bed subscription\b/g, 'the subscription');
}
export function withPricingWords(r: SalesKitSection): SalesKitSection {
  const text = pricingWords(r.text);
  return text === r.text ? r : { ...r, text };
}

/** A search result row as an index entry (lines to say, objections and stall points are "say"). */
export function itemOf(r: SalesKitSection): KitItem {
  const say = r.kind === 'say' || r.kind === 'objection' || r.kind === 'stall';
  return { doc: r.doc, sec: r.section, label: say ? (r.label ?? r.section).replace(/ /g, ' ') : r.section, text: r.text, say, obj: r.kind === 'objection' || r.kind === 'stall', stall: r.kind === 'stall' };
}

/** The unsearched list: every line to say, then the key points (not the "what it covers" notes). */
export function defaultHits(idx: KitItem[]): KitItem[] {
  return idx.filter((it) => it.say).concat(idx.filter((it) => !it.say && it.sec !== 'What it covers' && it.sec !== 'How to use it'));
}

/** Split a paragraph into its lead sentence and up to three points (design kitHits). */
export function sentences(text: string): string[] {
  return String(text || '')
    .split(/(?<=[.?!])\s+(?=[A-Z0-9“"$])/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export interface KitObjection {
  they: string;
  you: string;
  doc: string;
  stall: boolean;
}

/**
 * The Objections table (design OBJ): every "IF THEY ASK ..." line and every objection,
 * once each, then the Sales FAQ's sections as questions.
 */
export function kitObjections(idx: KitItem[], kit: KitDoc[]): KitObjection[] {
  const out: KitObjection[] = [];
  const seen = new Set<string>();
  for (const it of idx) {
    let they: string | null = null;
    const m = it.label.match(/^IF(?: THEY (?:ASK|SAY))?\s*[“"](.+?)[”"]/i);
    if (m) they = m[1].charAt(0) + m[1].slice(1).toLowerCase();
    else if (it.obj) they = it.label.replace(/^[“"]|[”"]$/g, '');
    if (!they || seen.has(they.toLowerCase())) continue;
    seen.add(they.toLowerCase());
    out.push({ they, you: it.text, doc: it.doc, stall: !!it.stall });
  }
  const faq = kit.find((d) => d.doc === 'Sales FAQ');
  for (const sec of faq?.sections ?? []) if (sec.name !== 'What it covers') out.push({ they: sec.name, you: sec.body, doc: 'Sales FAQ', stall: false });
  return out;
}

/** The figure a reply leans on, pulled from its text (design proof()); an en dash when none. The window
 * holds "$99 per occupied bed per month" whole (the design's 26 characters cut its last letter). */
export function proofOf(t: string): string {
  const m = t.match(/\$[\d,.]+[^.;,]{0,28}|\b\d[\d,.]*%?\s(?:per|day|beds?|facilities|minutes|questions|devices)[^.;,]{0,22}/i);
  return m ? m[0].trim() : '–';
}

/** "the Field Card, Sales Cycle Playbook and Sales FAQ": the documents the table draws from. */
export function sourceList(docs: string[]): string {
  const names = [...new Set(docs)].map((d) => d.split(' · ')[0]);
  if (!names.length) return '';
  const list = names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  return 'the ' + list;
}

/** The whole document as plain text (Copy all). */
export function docText(d: KitDoc): string {
  const out: string[] = [d.doc];
  if (d.sub) out.push(d.sub);
  for (const s of d.sections) {
    out.push('');
    if (s.step) out.push(s.step);
    out.push(s.name);
    if (s.body) out.push(s.body);
    for (const q of s.quotes) out.push(q[0].replace(/ /g, ' ') + ': ' + q[1]);
    for (const b of s.bullets) out.push('- ' + b);
    if (s.flag) out.push(s.flag);
    for (const o of s.objections) out.push(o[0] + ' ' + o[1]);
  }
  return out.join('\n');
}

/** Write to the clipboard; a browser that refuses is not an error the rep can act on. */
export function copyText(text: string): void {
  try {
    const p = navigator.clipboard?.writeText(text);
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    /* no clipboard in this context */
  }
}

